import { FileIcon, ImageIcon, FileTextIcon, X } from 'lucide-react';
import { clsx } from 'clsx';

interface FileCardProps {
  name: string;
  size?: number;
  mimeType?: string;
  url?: string;
  onRemove?: () => void;
  className?: string;
  uploading?: boolean;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileIcon(mimeType?: string) {
  if (!mimeType) return FileIcon;
  if (mimeType.startsWith('image/')) return ImageIcon;
  if (mimeType.includes('pdf') || mimeType.includes('text')) return FileTextIcon;
  return FileIcon;
}

export function FileCard({
  name,
  size,
  mimeType,
  url,
  onRemove,
  className,
  uploading = false,
}: FileCardProps) {
  const Icon = getFileIcon(mimeType);

  const content = (
    <div
      className={clsx(
        'flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-lg group',
        uploading && 'opacity-60',
        url && 'hover:border-gray-300',
        className
      )}
    >
      <div className="flex-shrink-0 p-2 bg-gray-100 rounded-lg">
        <Icon className="h-5 w-5 text-gray-500" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-700 truncate">{name}</p>
        {size && (
          <p className="text-xs text-gray-400">{formatFileSize(size)}</p>
        )}
        {uploading && (
          <p className="text-xs text-primary-600">Uploading...</p>
        )}
      </div>
      {onRemove && !uploading && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onRemove();
          }}
          className="flex-shrink-0 p-1 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );

  if (url && !uploading) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="block"
      >
        {content}
      </a>
    );
  }

  return content;
}
